import React from 'react';
import AppActionMenu from '../../../../components/AppActionMenu';
import { getStatusBadgeClass } from '../../../../constants/statusColors';

export default function AttendanceHistoryTable({
  records = [],
  isLoading = false,
  onViewSwipes,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="text-xs font-semibold text-on-surface-variant">Loading attendance records...</span>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-64 w-full flex-col items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-8 text-center">
        <span className="material-symbols-outlined mb-2 text-4xl text-outline-variant">
          history_toggle_off
        </span>
        <h3 className="font-display text-sm font-bold text-black">No Attendance Records Found</h3>
        <p className="mt-1 text-xs text-slate-600 max-w-sm">
          No punch records matched your selected date range and filter criteria.
        </p>
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            <span>Reset Filters</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-outline-variant/25 bg-surface-container-low/40 text-[11px] font-extrabold uppercase tracking-wider text-black">
              <th className="py-3.5 px-6 text-black">NAME</th>
              <th className="py-3.5 px-6 text-black">DATE</th>
              <th className="py-3.5 px-6 text-black">FIRST PUNCH</th>
              <th className="py-3.5 px-6 text-black">LAST PUNCH</th>
              <th className="py-3.5 px-6 text-center text-black">ATTENDANCE</th>
              <th className="py-3.5 px-6 text-right w-20 text-black">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((record, index) => {
              const cleanDate = String(record.date || '').replace(/\s+12:00:00\s*(AM|PM)?/i, '');
              const hasFirstPunch = record.firstPunch && record.firstPunch !== '-';
              const hasLastPunch = record.lastPunch && record.lastPunch !== '-';

              return (
                <tr
                  key={record.id || index}
                  className="transition-colors hover:bg-surface-container-low/50 group"
                >
                  {/* NAME */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="font-bold text-sm text-black font-sans not-italic">
                        {record.name || 'Divyanshu Singh'}
                      </span>
                      {record.empId && (
                        <span className="text-xs text-slate-600 font-sans tabular-nums not-italic font-semibold ml-1.5">
                          ({record.empId})
                        </span>
                      )}
                    </div>
                  </td>

                  {/* DATE */}
                  <td className="py-4 px-6 font-sans tabular-nums font-normal text-sm text-on-surface whitespace-nowrap">
                    {cleanDate}
                  </td>

                  {/* FIRST PUNCH */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    {hasFirstPunch ? (
                      <div className="inline-flex items-center gap-1.5 font-sans tabular-nums font-semibold text-sm text-emerald-600">
                        <span className="material-symbols-outlined text-base text-emerald-600 leading-none select-none">
                          login
                        </span>
                        <span>{record.firstPunch}</span>
                      </div>
                    ) : (
                      <span className="text-[#9ca3af] font-sans font-normal text-sm pl-1">—</span>
                    )}
                  </td>

                  {/* LAST PUNCH */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    {hasLastPunch ? (
                      <div className="inline-flex items-center gap-1.5 font-sans tabular-nums font-semibold text-sm text-on-surface">
                        <span className="material-symbols-outlined text-base text-slate-500 leading-none select-none">
                          logout
                        </span>
                        <span>{record.lastPunch}</span>
                      </div>
                    ) : (
                      <span className="text-[#9ca3af] font-sans font-normal text-sm pl-1">—</span>
                    )}
                  </td>

                  {/* ATTENDANCE (Using global soft-tint pill format matching Pay Heads) */}
                  <td className="py-4 px-6 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold ${getStatusBadgeClass(
                        record.attendance
                      )}`}
                    >
                      {record.attendance}
                    </span>
                  </td>

                  {/* ACTIONS */}
                  <td className="py-4 px-6 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end">
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1 text-[#4b5563] hover:text-black hover:bg-surface-container-high transition bg-transparent cursor-pointer"
                        items={[
                          {
                            key: 'view-swipes',
                            label: 'View All Swipes',
                            icon: 'history',
                            onClick: () => {
                              onViewSwipes?.(record);
                            },
                          },
                          {
                            key: 'print',
                            label: 'Print Day Summary',
                            icon: 'print',
                            onClick: () => window.print(),
                          },
                        ]}
                        onSelect={(key) => {
                          if (key === 'view-swipes') {
                            onViewSwipes?.(record);
                          } else if (key === 'print') {
                            window.print();
                          }
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
